import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  RiArrowLeftLine,
  RiAddLine,
  RiStickyNoteLine,
  RiChat3Line,
  RiCodeLine,
  RiFileCodeLine,
  RiContactsLine
} from '@remixicon/react';
import { Button, Card, CardBody, Chip, Switch, Spinner, Accordion, AccordionItem, Tooltip } from '@heroui/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'react-toastify';
import DefaultLayout from '@/layouts/default';
import { leadDetailQueryAtom, leadDetailIdAtom, leadDetailIncludeRawAtom, assignLeadOwner } from '@/store';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { UserCell } from '@/components/shared/UserCell';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { LeadNoteItem } from '@/components/leads/LeadNoteItem';
import { LeadInteractionItem } from '@/components/leads/LeadInteractionItem';
import { LeadContactItem } from '@/components/leads/LeadContactItem';
import { AssignOwnerModal } from '@/components/leads/AssignOwnerModal';
import { AddNoteModal } from '@/components/leads/AddNoteModal';
import { AddInteractionModal } from '@/components/leads/AddInteractionModal';
import { AddContactModal } from '@/components/leads/AddContactModal';
import {
  useLeadAssignOwner,
  useLeadAssignSelf,
  useLeadStatusChange,
  useLeadNotes,
  useLeadInteractions,
  useLeadContacts
} from '@/hooks/leads';
import { AssignSelfConfirmModal } from '@/components/leads/AssignSelfConfirmModal';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';
import type { LeadStatus } from '@/types';
import { JsonViewer } from '@/components/shared/JsonViewer';

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [leadId, setLeadId] = useAtom(leadDetailIdAtom);
  const [includeRaw, setIncludeRaw] = useAtom(leadDetailIncludeRawAtom);
  const { data, isLoading, isError } = useAtomValue(leadDetailQueryAtom);

  const { isModalOpen, selectedLead, isSubmitting, openModal, handleAssign, onModalOpenChange } = useLeadAssignOwner();
  const {
    isConfirmOpen: isAssignSelfOpen,
    selectedLead: selectedLeadForSelf,
    isSubmitting: isAssigningSelf,
    openConfirm: openAssignSelf,
    onConfirm: handleAssignSelf,
    onOpenChange: onAssignSelfOpenChange
  } = useLeadAssignSelf();
  const { isUpdating, handleStatusChange } = useLeadStatusChange();
  const {
    isAddModalOpen: isAddNoteModalOpen,
    selectedLead: selectedLeadForNote,
    isSubmitting: isSubmittingNote,
    isDeleting: isDeletingNote,
    openAddModal: openAddNoteModal,
    handleCreateNote,
    handleDeleteNote,
    isDeleteModalOpen: isDeleteNoteModalOpen,
    openDeleteModal: openDeleteNoteModal,
    noteToDelete,
    onAddModalOpenChange: onAddNoteModalOpenChange,
    onDeleteModalOpenChange: onDeleteNoteModalOpenChange
  } = useLeadNotes();
  const {
    isAddModalOpen: isAddInteractionModalOpen,
    selectedLead: selectedLeadForInteraction,
    isSubmitting: isSubmittingInteraction,
    isDeleting: isDeletingInteraction,
    openAddModal: openAddInteractionModal,
    handleCreateInteraction,
    handleDeleteInteraction,
    isDeleteModalOpen: isDeleteInteractionModalOpen,
    openDeleteModal: openDeleteInteractionModal,
    interactionToDelete,
    onAddModalOpenChange: onAddInteractionModalOpenChange,
    onDeleteModalOpenChange: onDeleteInteractionModalOpenChange
  } = useLeadInteractions();
  const {
    isAddModalOpen: isAddContactModalOpen,
    selectedLead: selectedLeadForContact,
    isSubmitting: isSubmittingContact,
    deletingContactId,
    openAddModal: openAddContactModal,
    handleCreateContact,
    handleDeleteContact,
    onAddModalOpenChange: onAddContactModalOpenChange
  } = useLeadContacts();
  const clearOwner = useSetAtom(assignLeadOwner);
  const [isClearOwnerConfirmOpen, setIsClearOwnerConfirmOpen] = React.useState(false);

  React.useEffect(() => {
    if (id) {
      setLeadId(Number(id));
    }
  }, [id, setLeadId]);

  const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
    new: 'default',
    contacted: 'primary',
    followup: 'warning',
    closed: 'success',
    dispensable: 'danger'
  };
  const statusOptions: LeadStatus[] = ['new', 'contacted', 'followup', 'closed', 'dispensable'];

  const [statusDraft, setStatusDraft] = React.useState<LeadStatus>('new');
  const [statusDirty, setStatusDirty] = React.useState(false);

  const lead = data?.data;
  const handleClearOwner = React.useCallback(async () => {
    if (!lead) return;
    try {
      await clearOwner({ leadId: lead.id, ownerId: null });
      toast.success('Owner removed successfully');
    } catch (error) {
      console.error('Failed to remove owner', error);
    } finally {
      setIsClearOwnerConfirmOpen(false);
    }
  }, [clearOwner, lead]);

  React.useEffect(() => {
    if (lead) {
      setStatusDraft(lead.status);
      setStatusDirty(false);
    }
  }, [lead?.status]);

  const handleStatusDraftChange = React.useCallback(
    (value: LeadStatus) => {
      setStatusDraft(value);
      if (lead) {
        setStatusDirty(value !== lead.status);
      }
    },
    [lead]
  );

  const handleStatusSubmit = React.useCallback(async () => {
    if (!lead || !statusDirty) return;
    await handleStatusChange(lead.id, statusDraft);
    setStatusDirty(false);
  }, [lead, statusDirty, statusDraft, handleStatusChange]);

  if (isLoading) {
    return (
      <DefaultLayout>
        <div className="flex items-center justify-center p-8">
          <Spinner size="lg" />
        </div>
      </DefaultLayout>
    );
  }

  if (isError || !lead) {
    return (
      <DefaultLayout>
        <div className="p-8">
          <Card>
            <CardBody className="p-6">
              <p className="text-danger">Failed to load lead details</p>
              <Button className="mt-4" onPress={() => navigate('/relationships/leads')}>
                Back to Leads
              </Button>
            </CardBody>
          </Card>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button isIconOnly variant="light" onPress={() => navigate('/relationships/leads')}>
              <RiArrowLeftLine className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{lead.domain}</h1>
              {lead.title && <p className="text-default-500 mt-1 text-sm">{lead.title}</p>}
              <p className="text-default-500 text-sm">Lead Details</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <PermissionGuard permission="Lead.update">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="border-default-200 bg-background rounded-lg border p-2 text-sm"
                  value={statusDraft}
                  onChange={(e) => handleStatusDraftChange(e.target.value as LeadStatus)}
                  disabled={isUpdating}
                >
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </option>
                  ))}
                </select>
                {statusDirty && (
                  <Button size="sm" color="primary" onPress={handleStatusSubmit} isLoading={isUpdating}>
                    Update
                  </Button>
                )}
              </div>
            </PermissionGuard>

            {!lead.owner && (
              <PermissionGuard permission="Lead.assignSelf">
                <Button color="primary" onPress={() => openAssignSelf(lead)} className="flex-shrink-0">
                  Assign to me
                </Button>
              </PermissionGuard>
            )}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-7">
          <div className="space-y-3 md:col-span-5">
            {/* Description Section */}
            {lead.description && (
              <Card>
                <CardBody className="p-6">
                  <h3 className="mb-3 text-lg font-semibold">Description</h3>
                  <p className="text-default-700 whitespace-pre-wrap">{lead.description}</p>
                </CardBody>
              </Card>
            )}

            {/* Interactions Section */}
            <Card>
              <CardBody className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-lg font-semibold">
                    <RiChat3Line className="text-default-500 h-5 w-5" />
                    Interactions ({lead.interactions?.length || 0})
                  </h2>
                  <PermissionGuard permission="LeadInteraction.store">
                    <Button
                      size="sm"
                      color="primary"
                      startContent={<RiAddLine className="size-4 text-white" />}
                      onPress={() => openAddInteractionModal(lead)}
                    >
                      <span className="text-white">Add Interaction</span>
                    </Button>
                  </PermissionGuard>
                </div>
                <div className="space-y-3">
                  {lead.interactions && lead.interactions.length > 0 ? (
                    lead.interactions.map((interaction) => (
                      <LeadInteractionItem
                        key={interaction.id}
                        interaction={interaction}
                        onDelete={() => openDeleteInteractionModal(lead, interaction)}
                        isDeleting={isDeletingInteraction && interactionToDelete?.id === interaction.id}
                      />
                    ))
                  ) : (
                    <p className="text-default-500 text-center text-sm">No interactions yet</p>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Contacts Section */}
            <Card>
              <CardBody className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-lg font-semibold">
                    <RiContactsLine className="text-default-500 h-5 w-5" />
                    Contacts ({lead.contacts?.length || 0})
                  </h2>
                  <PermissionGuard permission="LeadContact.store">
                    <Button
                      size="sm"
                      color="success"
                      startContent={<RiAddLine className="size-4 text-white" />}
                      onPress={() => openAddContactModal(lead)}
                    >
                      <span className="text-white">Add Contact</span>
                    </Button>
                  </PermissionGuard>
                </div>
                <div className="space-y-3">
                  {lead.contacts && lead.contacts.length > 0 ? (
                    lead.contacts.map((contact) => (
                      <LeadContactItem
                        key={contact.id}
                        contact={contact}
                        onDelete={(contactId) => handleDeleteContact(lead.id, contactId)}
                        isDeleting={deletingContactId === contact.id}
                      />
                    ))
                  ) : (
                    <p className="text-default-500 text-center text-sm">No contacts yet</p>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Notes Section */}
            <Card>
              <CardBody className="p-6">
                <div className="items_center mb-4 flex flex-wrap justify-between gap-2">
                  <h2 className="flex items-center gap-2 text-lg font-semibold">
                    <RiStickyNoteLine className="text-default-500 h-5 w-5" />
                    Notes ({lead.notes?.length || 0})
                  </h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <PermissionGuard permission="LeadNote.store">
                      <Button
                        size="sm"
                        color="warning"
                        startContent={<RiAddLine className="size-4 text-white" />}
                        onPress={() => openAddNoteModal(lead)}
                      >
                        <span className="text-white">Add Note</span>
                      </Button>
                    </PermissionGuard>
                  </div>
                </div>
                <div className="space-y-3">
                  {lead.notes && lead.notes.length > 0 ? (
                    lead.notes.map((note) => (
                      <LeadNoteItem
                        key={note.id}
                        note={note}
                        onDelete={() => openDeleteNoteModal(lead, note)}
                        isDeleting={isDeletingNote && noteToDelete?.id === note.id}
                      />
                    ))
                  ) : (
                    <p className="text-default-500 text-center text-sm">No notes yet</p>
                  )}
                </div>
              </CardBody>
            </Card>

            {lead.raw && (
              <Accordion variant="splitted" className="!px-0">
                <AccordionItem
                  title={
                    <span className="flex items-center gap-2">
                      <RiFileCodeLine className="text-default-500 h-5 w-5" /> Raw Data
                    </span>
                  }
                  classNames={{ content: 'pt-0' }}
                  aria-label="Raw Data"
                >
                  <div className="border-default-200 bg-default-50 max-h-96 overflow-auto rounded-lg border p-4 shadow-inner">
                    <JsonViewer data={lead.raw} maxHeightPx={360} unstyled className="bg-transparent p-0" />
                  </div>
                </AccordionItem>
              </Accordion>
            )}
          </div>

          <div className="space-y-6 md:col-span-2">
            {/* Info card */}
            <Card>
              <CardBody className="p-6">
                <h3 className="mb-4 text-lg font-semibold">Info</h3>
                <div className="divide-default-100 divide-y text-sm">
                  {[
                    { label: 'Score', value: lead.score !== null ? `${(lead.score * 100).toFixed(0)}%` : '-' },
                    { label: 'Competitors', value: lead.competitor_count },
                    { label: 'Status', value: lead.status },
                    { label: 'Script Match', value: lead.has_script_match ? 'Yes' : 'No' },
                    { label: 'JS Required', value: lead.js_required ? 'Yes' : 'No' },
                    { label: 'E-commerce', value: lead.is_ecommerce ? 'Yes' : 'No' },
                    {
                      label: 'E-commerce Platform',
                      value: lead.ecommerce_platform
                        ? `${lead.ecommerce_platform.name} (${(lead.ecommerce_platform.score * 100).toFixed(0)}%)`
                        : '-'
                    },
                    { label: 'Fetch Mode', value: lead.fetch_mode || '-' },
                    { label: 'Render Status', value: lead.render_status || '-' },
                    {
                      label: 'Collected At',
                      value: lead.collected_at ? (
                        <DateTimeDisplay className="text-right" dateString={lead.collected_at} />
                      ) : (
                        '-'
                      )
                    },
                    {
                      label: 'Synced At',
                      value: lead.synced_at ? (
                        <DateTimeDisplay className="text-right" dateString={lead.synced_at} />
                      ) : (
                        '-'
                      )
                    },
                    {
                      label: 'Created At',
                      value: lead.created_at ? (
                        <DateTimeDisplay className="text-right" dateString={lead.created_at} />
                      ) : (
                        '-'
                      )
                    },
                    {
                      label: 'Updated At',
                      value: lead.updated_at ? (
                        <DateTimeDisplay className="text-right" dateString={lead.updated_at} />
                      ) : (
                        '-'
                      )
                    }
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between gap-4 py-2">
                      <span className="text-default-500">{item.label}</span>
                      <div className="text-default-900 text-right font-medium">{item.value}</div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* Owner Section */}
            <Card>
              <CardBody className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="mb-2 text-lg font-semibold">Owner</h3>
                    {lead.owner ? (
                      <UserCell user={lead.owner} />
                    ) : (
                      <p className="text-default-500 text-sm">No owner assigned</p>
                    )}
                  </div>
                </div>
                <PermissionGuard permission="Lead.assignOwner">
                  <div className="mt-4 flex items-center justify-end gap-2">
                    {lead.owner ? (
                      <>
                        <Button size="sm" variant="flat" color="primary" onPress={() => openModal(lead)}>
                          Change Owner
                        </Button>
                        <Tooltip content="Remove owner">
                          <Button
                            size="sm"
                            variant="light"
                            color="danger"
                            onPress={() => setIsClearOwnerConfirmOpen(true)}
                          >
                            Remove owner
                          </Button>
                        </Tooltip>
                      </>
                    ) : (
                      <Button size="sm" color="primary" onPress={() => openModal(lead)}>
                        Assign Owner
                      </Button>
                    )}
                  </div>
                </PermissionGuard>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AssignOwnerModal
        isOpen={isModalOpen}
        onOpenChange={onModalOpenChange}
        lead={selectedLead}
        isSubmitting={isSubmitting}
        onAssign={handleAssign}
      />

      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onOpenChange={onAddNoteModalOpenChange}
        lead={selectedLeadForNote}
        isSubmitting={isSubmittingNote}
        onSubmit={handleCreateNote}
      />

      <AddInteractionModal
        isOpen={isAddInteractionModalOpen}
        onOpenChange={onAddInteractionModalOpenChange}
        lead={selectedLeadForInteraction}
        isSubmitting={isSubmittingInteraction}
        onSubmit={handleCreateInteraction}
      />
      <AddContactModal
        isOpen={isAddContactModalOpen}
        onOpenChange={onAddContactModalOpenChange}
        lead={selectedLeadForContact}
        isSubmitting={isSubmittingContact}
        onSubmit={(type, value, source) => handleCreateContact(type, value, source)}
      />
      <AssignSelfConfirmModal
        isOpen={isAssignSelfOpen}
        onOpenChange={onAssignSelfOpenChange}
        lead={selectedLeadForSelf}
        isSubmitting={isAssigningSelf}
        onConfirm={handleAssignSelf}
      />
      <ConfirmDeleteModal
        isOpen={isDeleteNoteModalOpen}
        onOpenChange={onDeleteNoteModalOpenChange}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        isLoading={isDeletingNote}
        onConfirm={handleDeleteNote}
      />
      <ConfirmDeleteModal
        isOpen={isDeleteInteractionModalOpen}
        onOpenChange={onDeleteInteractionModalOpenChange}
        title="Delete Interaction"
        message="Are you sure you want to delete this interaction? This action cannot be undone."
        confirmText="Delete"
        isLoading={isDeletingInteraction}
        onConfirm={handleDeleteInteraction}
      />
      <ConfirmDeleteModal
        isOpen={isClearOwnerConfirmOpen}
        onOpenChange={setIsClearOwnerConfirmOpen}
        title="Remove Owner"
        message="Are you sure you want to remove the owner from this lead?"
        confirmText="Remove"
        isLoading={false}
        onConfirm={handleClearOwner}
      />
    </DefaultLayout>
  );
}
